import React from "react";
import {
    DrawerRouter,
    useNavigationBuilder,
    createNavigatorFactory,
    StackRouter,
} from "@react-navigation/native";
import { StackView } from "@react-navigation/stack";
import { DrawerView } from "@react-navigation/drawer";
import { nanoid } from "nanoid/non-secure";

export enum RouterType {
    Root = "Root",
    Quiz = "Quiz",
    Home = "Home",
}

const RootRouter = options => {
    const router = DrawerRouter(options);

    return {
        ...router,
        getStateForAction(state, action, options) {
            // console.info("Root getStateForAction: ", state, action);
            const newState = router.getStateForAction(state, action, options);

            let shouldNavigateToRoot = state.routeNames.reduce(
                (acc, routeName) => {
                    if (acc) return acc;

                    return action.payload?.name === routeName;
                },
                false,
            );

            if (shouldNavigateToRoot) {
                if (action.payload?.params?._meta?.shouldResetRoot) {
                    const routes = state.routes.map(route => {
                        const { name, key } = route;

                        return {
                            isRoot: true,
                            params: undefined,
                            name,
                            key,
                        };
                    });

                    const index = routes.indexOf(
                        routes.find(
                            route => route.name === action.payload?.name,
                        ),
                    );

                    const history = [];

                    const result = {
                        ...state,
                        routes,
                        history,
                        index,
                    };

                    return result;
                }
            }

            if (action.payload?.name === "Strategy") {
                const { strategyId, _meta } = action.payload.params;
                const myStrategiesRoute = state.routes.find(
                    route => route.name === "MyStrategies",
                );

                const myStrategiesRootRoute = {
                    name: "MyStrategies",
                    key: `MyStrategies-${nanoid()}`,
                    params: undefined,
                };

                const strategyCategoryRoute = {
                    name: "StrategyCategory",
                    key: `StrategyCategory-${nanoid()}`,
                    params: {
                        strategyCategoryId: _meta.strategyCategoryId,
                    },
                };

                const strategyRoute = {
                    name: "Strategy",
                    key: `Strategy-${nanoid()}`,
                    params: {
                        strategyId,
                    },
                };

                const myStrategiesState = {
                    stale: false,
                    type: "stack",
                    key: `stack-${nanoid()}`,
                    index: 2,
                    routeNames: [
                        "MyStrategies",
                        "StrategyCategory",
                        "Strategy",
                    ],
                    routes: [
                        myStrategiesRootRoute,
                        strategyCategoryRoute,
                        strategyRoute,
                    ],
                };

                const history = [
                    {
                        type: "route",
                        key: myStrategiesRoute.key,
                    },
                ];

                const routes = state.routes.map(route => {
                    const { name, key } = route;

                    if (route.name === "MyStrategies") {
                        return {
                            isRoot: true,
                            params: undefined,
                            state: myStrategiesState,
                            name,
                            key,
                        };
                    }

                    return {
                        isRoot: true,
                        params: undefined,
                        name,
                        key,
                    };
                });

                const index = routes.indexOf(
                    routes.find(route => route.name === "MyStrategies"),
                );

                const result = {
                    ...state,
                    routes,
                    history,
                    index,
                };

                return result;
            }

            return newState;
        },
    };
};

const HomeRouter = options => {
    const router = StackRouter(options);

    return {
        ...router,
        getStateForAction(state, action, options) {
            // console.info("Home getStateForAction: ", state, action);
            const newState = router.getStateForAction(state, action, options);
            const quizRoute = state.routes.find(route => route.name === "Quiz");
            const currentQuizId = quizRoute?.params?.quizId;
            const nextQuizId = action.payload?.params?.quizId;
            // isReview is passed through from the completed list of quizzes
            // this sets the display mode of QuizReview
            // on first run-thru of a quiz one "redo" link is displayed
            // on subsequent, a review cta with more info is displayed
            const isReview = action.payload?.params?.isReview || false;
            // capture any other params not accounted for
            const passThruParams = action.payload?.params || {};

            const isIntraQuizNavigation =
                quizRoute &&
                action.payload?.name === "Quiz" &&
                nextQuizId &&
                nextQuizId !== currentQuizId;

            if (isIntraQuizNavigation) {
                const newQuizRoute = {
                    key: `Quiz_${nextQuizId}_${nanoid()}`,
                    name: "Quiz",
                    params: {
                        quizId: nextQuizId,
                        isReview,
                        ...passThruParams,
                    },
                    state: router.getInitialState(options),
                };

                const result = {
                    ...state,
                    index: 1,
                    routes: [state.routes[0], newQuizRoute],
                };

                return result;
            }

            return newState;
        },
    };
};

const createNavigatorComponent = (type: RouterType) => {
    let Router: any = StackRouter;
    let RouterView: any = StackView;

    switch (type) {
        case RouterType.Root:
            Router = RootRouter;
            RouterView = DrawerView;
            break;

        case RouterType.Home:
        case RouterType.Quiz:
            Router = HomeRouter;
            break;
    }

    const NavigatorComponent = ({
        initialRouteName,
        children,
        screenOptions,
        ...rest
    }) => {
        const { state, navigation, descriptors } = useNavigationBuilder(
            Router,
            {
                initialRouteName,
                children,
                screenOptions,
            },
        );

        return (
            <RouterView
                state={state}
                navigation={navigation}
                descriptors={descriptors}
                {...rest}
            />
        );
    };

    return NavigatorComponent;
};

export const createStackNavigator = (type: RouterType) => {
    const NavigatorComponent = createNavigatorComponent(type);
    const factory = createNavigatorFactory(NavigatorComponent);

    return factory();
};

export const createDrawerNavigator = (type: RouterType) => {
    const NavigatorComponent = createNavigatorComponent(type);
    const factory = createNavigatorFactory(NavigatorComponent);

    return factory();
};
